/**
 * OrgChangeDualPath — Step 2 of OrgChangeStepper (v0.14 Wave 3)
 *
 * Sub-modal that runs when the Step 1 picker returned a DBA match. Per spec
 * §14.0 — LOCKED VERBATIM copy. Do NOT paraphrase, do NOT "improve."
 *
 * Heading: "How should we bill {DBA name}?"
 *
 * Option 1 (default radio, recommended badge):
 *   Label: "Bill under {parent client name} with {DBA name} as the DBA on the invoice"
 *   Sub-copy: "Recommended — {DBA name} is already set up as a DBA under {parent client name}."
 *
 * Option 2:
 *   Label: "Bill directly as {DBA name} as a brand new, separate client (not connected to {parent client name})"
 *   Sub-copy: "Use this if {DBA name} is its own client, not part of {parent client name}'s business."
 *
 * Default = Option 1. Recommended badge + primary button color on Option 1 per
 * Christa-UX footgun #1 (she picks first/highlighted ~80% of the time).
 *
 * No "parent org" string anywhere per §0.2 vocabulary bans. Uses "parent
 * client name" tokens, not "parent org."
 */
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface OrgChangeDualPathProps {
  dbaName: string;
  parentClientName: string;
  choice: 'parent' | 'standalone' | null;
  onChoiceChange: (c: 'parent' | 'standalone') => void;
  onContinue: () => void;
  onBack: () => void;
}

export function OrgChangeDualPath({
  dbaName,
  parentClientName,
  choice,
  onChoiceChange,
  onContinue,
  onBack,
}: OrgChangeDualPathProps) {
  // Default to 'parent' on initial render (locked per §14.0).
  const effectiveChoice = choice ?? 'parent';
  const canContinue = effectiveChoice === 'parent' || effectiveChoice === 'standalone';

  return (
    <div className="space-y-4" data-testid="org-change-dual-path">
      {/* LOCKED VERBATIM heading per §14.0 */}
      <h3
        className="text-base font-semibold text-slate-900"
        data-testid="org-change-dual-path-heading"
      >
        How should we bill {dbaName}?
      </h3>

      <div className="space-y-2">
        {/* Option 1 — LOCKED VERBATIM */}
        <label
          className={`block border rounded-md p-3 cursor-pointer transition-colors ${
            effectiveChoice === 'parent'
              ? 'border-blue-500 bg-blue-50'
              : 'border-slate-200 hover:border-slate-300'
          }`}
          data-testid="org-change-dual-path-option-parent"
        >
          <div className="flex items-start gap-2">
            <input
              type="radio"
              name="org-change-dual-path"
              value="parent"
              checked={effectiveChoice === 'parent'}
              onChange={() => onChoiceChange('parent')}
              className="mt-1"
              data-testid="org-change-dual-path-radio-parent"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-2 flex-wrap">
                <span
                  className="text-sm font-medium text-slate-900"
                  data-testid="org-change-dual-path-option-parent-label"
                >
                  <span className="font-semibold">
                    Bill under {parentClientName}
                  </span>{' '}
                  with {dbaName} as the DBA on the invoice
                </span>
                <span
                  className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-green-100 text-green-800 border border-green-200"
                  data-testid="org-change-dual-path-recommended-badge"
                >
                  Recommended
                </span>
              </div>
              <p
                className="text-xs text-slate-600 mt-1"
                data-testid="org-change-dual-path-option-parent-sub"
              >
                Recommended &mdash; {dbaName} is already set up as a DBA under{' '}
                {parentClientName}.
              </p>
            </div>
          </div>
        </label>

        {/* Option 2 — LOCKED VERBATIM */}
        <label
          className={`block border rounded-md p-3 cursor-pointer transition-colors ${
            effectiveChoice === 'standalone'
              ? 'border-blue-500 bg-blue-50'
              : 'border-slate-200 hover:border-slate-300'
          }`}
          data-testid="org-change-dual-path-option-standalone"
        >
          <div className="flex items-start gap-2">
            <input
              type="radio"
              name="org-change-dual-path"
              value="standalone"
              checked={effectiveChoice === 'standalone'}
              onChange={() => onChoiceChange('standalone')}
              className="mt-1"
              data-testid="org-change-dual-path-radio-standalone"
            />
            <div className="flex-1 min-w-0">
              <span
                className="text-sm font-medium text-slate-900"
                data-testid="org-change-dual-path-option-standalone-label"
              >
                <span className="font-semibold">Bill directly as {dbaName}</span>{' '}
                as a brand new, separate client (not connected to{' '}
                {parentClientName})
              </span>
              <p
                className="text-xs text-slate-600 mt-1"
                data-testid="org-change-dual-path-option-standalone-sub"
              >
                Use this if {dbaName} is its own client, not part of{' '}
                {parentClientName}&apos;s business.
              </p>
            </div>
          </div>
        </label>
      </div>

      <div className="flex items-center justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
          data-testid="org-change-dual-path-back"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to picker
        </button>
        <Button
          onClick={onContinue}
          disabled={!canContinue}
          data-testid="org-change-dual-path-continue"
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
